import React from 'react';
import { Link } from 'react-router-dom';
import { complianceFeatures } from '../mockData';
import { Shield, CheckCircle, FileCheck, AlertTriangle, Eye, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

const Compliance = () => {
  return (
    <div style={{ backgroundColor: '#1a1c1b', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{
        padding: '120px 40px 80px',
        backgroundColor: '#302f2c',
        borderBottom: '1px solid rgba(63, 72, 22, 0.5)'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <Shield size={64} style={{ color: '#d9fb06', marginBottom: '24px', margin: '0 auto' }} />
          <h1 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 0.95,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '24px',
            marginTop: '24px'
          }}>
            Compliance & Safety
          </h1>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            maxWidth: '50rem',
            margin: '0 auto'
          }}>
            Built on a foundation of governance, safety, and due diligence
          </p>
        </div>
      </section>

      {/* Core Message */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#dfddd6',
            marginBottom: '32px'
          }}>
            Worklink Global is committed to the highest standards of workplace health and safety, regulatory compliance, and supplier accountability.
          </p>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.6,
            color: '#888680'
          }}>
            We rely on the <strong style={{ color: '#d9fb06' }}>client's and supplier's WHS systems</strong>, ensuring alignment across all stakeholders while maintaining oversight and accountability.
          </p>
        </div>
      </section>

      {/* Compliance Framework */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#302f2c'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Our Compliance Framework
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {complianceFeatures.map((feature, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: '#1a1c1b',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '1px solid rgba(63, 72, 22, 0.5)',
                  transition: 'background-color 0.3s ease',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3f4816'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a1c1b'}
              >
                <CheckCircle size={24} style={{ color: '#d9fb06', flexShrink: 0, marginTop: '2px' }} />
                <p style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  color: '#dfddd6',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  {feature}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Compliance Areas */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            Key Focus Areas
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px'
          }}>
            {[
              {
                icon: FileCheck,
                title: 'Documentation Control',
                description: 'Comprehensive management of insurances, licences, certifications, and WHS documentation for all suppliers and workers.'
              },
              {
                icon: Eye,
                title: 'Performance Monitoring',
                description: 'Regular supplier assessments, site visits, and performance audits to ensure consistent quality and safety standards.'
              },
              {
                icon: AlertTriangle,
                title: 'Risk Management',
                description: 'Proactive identification and mitigation of compliance risks with clear escalation procedures and corrective actions.'
              },
              {
                icon: Award,
                title: 'Qualification Verification',
                description: 'Thorough verification of trade qualifications, supervisor credentials, and industry-specific certifications.'
              }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#302f2c',
                    padding: '40px',
                    borderRadius: '8px',
                    border: '1px solid rgba(63, 72, 22, 0.5)',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <IconComponent size={48} style={{ color: '#d9fb06', marginBottom: '20px' }} />
                  <h3 style={{
                    fontFamily: 'Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    color: '#d9fb06',
                    marginBottom: '16px',
                    textTransform: 'uppercase'
                  }}>
                    {item.title}
                  </h3>
                  <p style={{
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#dfddd6'
                  }}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supplier Requirements */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#302f2c'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            Supplier Compliance Requirements
          </h2>
          <div style={{
            backgroundColor: '#1a1c1b',
            padding: '48px',
            borderRadius: '8px',
            border: '1px solid rgba(63, 72, 22, 0.5)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '32px'
            }}>
              <div>
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  color: '#d9fb06',
                  marginBottom: '16px',
                  textTransform: 'uppercase'
                }}>
                  Insurance Requirements
                </h3>
                <ul style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: '#dfddd6',
                  paddingLeft: '20px'
                }}>
                  <li>Workers' Compensation Insurance</li>
                  <li>Public Liability Insurance</li>
                  <li>Current and valid certificates</li>
                  <li>Adequate coverage levels</li>
                </ul>
              </div>
              <div>
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  color: '#d9fb06',
                  marginBottom: '16px',
                  textTransform: 'uppercase'
                }}>
                  Licences & Certifications
                </h3>
                <ul style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: '#dfddd6',
                  paddingLeft: '20px'
                }}>
                  <li>Trade licences and qualifications</li>
                  <li>Industry-specific certifications</li>
                  <li>Supervisor credentials</li>
                  <li>Current WHS training</li>
                </ul>
              </div>
              <div>
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  color: '#d9fb06',
                  marginBottom: '16px',
                  textTransform: 'uppercase'
                }}>
                  Worker Documentation
                </h3>
                <ul style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: '#dfddd6',
                  paddingLeft: '20px'
                }}>
                  <li>Police checks (where applicable)</li>
                  <li>Required vaccinations</li>
                  <li>English proficiency verification</li>
                  <li>Induction completion records</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Statement */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto',
          textAlign: 'center',
          backgroundColor: '#302f2c',
          padding: '48px',
          borderRadius: '8px',
          border: '1px solid rgba(63, 72, 22, 0.5)'
        }}>
          <Shield size={48} style={{ color: '#d9fb06', marginBottom: '24px', margin: '0 auto 24px' }} />
          <h3 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: '1.8rem',
            lineHeight: 1.2,
            color: '#d9fb06',
            marginBottom: '24px',
            textTransform: 'uppercase'
          }}>
            Zero Compromise on Safety
          </h3>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.7,
            color: '#dfddd6'
          }}>
            At Worklink Global, compliance is not optional. We maintain rigorous standards, conduct ongoing audits, and take immediate action on any non-compliance issues. Our clients and workers deserve nothing less than complete safety assurance.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#3f4816',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '24px',
            textTransform: 'uppercase'
          }}>
            Questions About Compliance?
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            marginBottom: '40px'
          }}>
            Our team is here to discuss our compliance framework and safety standards
          </p>
          <Link to="/contact" style={{ textDecoration: 'none' }}>
            <Button
              style={{
                background: '#d9fb06',
                color: '#1a1c1b',
                border: 'none',
                borderRadius: '10rem',
                padding: '1.2em 2em',
                fontFamily: 'Inter, Arial, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
